import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Star, CheckCircle2 } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_complaint-tracker-102/artifacts/5d2kmds0_IMG_2B71FDB9-74FD-4451-B622-B7FCD5B5DCBD.jpeg";

export default function RateServicePage() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const token = params.get("token");
  const [service, setService] = useState(null);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!id || !token) {
      setErr("Enlace inválido.");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/public/services/${id}/rating-info`, { params: { token } })
      .then((r) => {
        setService(r.data);
        if (r.data?.rating) setDone(true);
      })
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [id, token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!stars) {
      setErr("Selecciona al menos una estrella.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/services/${id}/rate`, {
        token,
        rating: stars,
        comment,
      });
      setDone(true);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-black px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="DS Travel Tours" className="w-10 h-10 object-cover" />
          <div>
            <div className="font-display text-base font-bold">DS Travel Tours</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Califica tu servicio
            </div>
          </div>
        </div>
        <Link
          to="/consulta"
          className="text-xs uppercase tracking-widest text-zinc-600 hover:text-black"
        >
          Consultar reserva
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10" data-testid="rate-page">
        {loading ? (
          <div className="text-center text-zinc-500 py-10">Cargando…</div>
        ) : err && !service ? (
          <div className="border-2 border-[#E63946] p-6 text-sm text-[#E63946]">{err}</div>
        ) : done ? (
          <div className="border-2 border-black p-10 text-center" data-testid="rate-done">
            <CheckCircle2 size={48} className="mx-auto mb-4" />
            <h1 className="font-display text-3xl font-bold mb-2">¡Gracias!</h1>
            <p className="text-zinc-600">Tu opinión ha sido registrada.</p>
          </div>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
              Reserva {service?.booking_number}
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight mb-6">
              ¿Cómo estuvo tu servicio?
            </h1>
            {service && (
              <div className="border border-black p-4 mb-6 text-sm bg-zinc-50">
                <div><b>Fecha:</b> {service.pickup_date} · {service.pickup_time}</div>
                <div><b>Trayecto:</b> {service.pickup_location} → {service.dropoff_location}</div>
                {service.driver_name && <div><b>Conductor:</b> {service.driver_name}</div>}
              </div>
            )}
            <form onSubmit={submit} className="border border-black p-6 bg-white">
              <div className="text-xs uppercase tracking-widest mb-3">Tu calificación</div>
              <div className="flex items-center gap-2 mb-6" data-testid="stars-row">
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = (hover || stars) >= n;
                  return (
                    <button
                      type="button"
                      key={n}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onClick={() => setStars(n)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`star-${n}`}
                      aria-label={`${n} estrellas`}
                    >
                      <Star
                        size={44}
                        strokeWidth={1.5}
                        fill={active ? "#000" : "none"}
                        className={active ? "text-black" : "text-zinc-400"}
                      />
                    </button>
                  );
                })}
              </div>
              <label className="block text-xs uppercase tracking-widest mb-2">
                Comentario (opcional)
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="rounded-none border-black min-h-[120px]"
                placeholder="Cuéntanos cómo fue tu experiencia…"
                data-testid="rating-comment"
              />
              {err && (
                <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2">
                  {err}
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
                  data-testid="submit-rating-btn"
                >
                  {submitting ? <Loader2 className="animate-spin" size={14} /> : "Enviar calificación"}
                </Button>
              </div>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
